import { useFormContext } from 'react-hook-form';

import {
  assignQuestionProperties,
  assignTopLevelScorecardProperties,
} from 'features/Connect/features/ConnectATSIntegration/utils';
import {
  ATSConnectHiringRequestCreateReferenceContactIds,
  ATSConnectHiringRequestRequestReferenceTestIds,
} from 'data-testids/ATS';
import { Box, Link, SelectChangeEvent, Stack, useTheme } from '@mui/material';
import {
  useQueryGetReferenceContacts,
  useQueryGetReferenceTemplates,
} from 'features/References/api';
import { ReferenceContactAndTemplateFormProps } from 'features/References';
import { ReferenceContact } from 'features/References/types/referenceTypes';
import { ContactTemplateSelect } from './ContactTemplateSelect';
import auth from 'utils/auth';

export const ReferenceContactAndTemplateForm: React.FC<ReferenceContactAndTemplateFormProps> = ({
  candidateId,
  newReferenceContactId,
  isCreatingNewReferenceCheck,
  onAddClick,
}) => {
  const theme = useTheme();
  const { setValue } = useFormContext();

  const districtId = auth.getUser().district?.id;

  const {
    data: referenceTemplates,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
  } = useQueryGetReferenceTemplates(districtId);
  const {
    data: referenceContacts,
    isLoading: isContactsLoading,
    isError: isContactsError,
  } = useQueryGetReferenceContacts(candidateId);

  const handleTemplateChange = (event: SelectChangeEvent, onChange: (id: number) => void) => {
    const selectedTemplateId = Number(event.target.value);
    const reference = referenceTemplates.find(
      (referenceTemplate) => referenceTemplate.id === selectedTemplateId
    );
    onChange(selectedTemplateId);

    if (isCreatingNewReferenceCheck && reference) {
      assignTopLevelScorecardProperties(
        reference.questions,
        reference.max_cumulative_score,
        setValue
      );
      assignQuestionProperties(reference.questions, setValue);
    }
  };

  const handleContactChange = (
    event: SelectChangeEvent,
    onChange: (contact: ReferenceContact) => void
  ) => {
    const selectedContactId = Number(event.target.value);
    const contact = referenceContacts.find((contact) => contact.id === selectedContactId);
    onChange(contact);
  };

  return (
    <Stack sx={{ pt: 1 }}>
      <ContactTemplateSelect
        dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.REFERENCE_CONTACT_SELECT}
        formId={'recipients'}
        handleChange={handleContactChange}
        isGetError={isContactsError}
        isLoading={isContactsLoading}
        items={referenceContacts}
        labelText="Reference Contacts"
        placeholderText="Select Contact"
        defaultValue={newReferenceContactId}
      />
      {!newReferenceContactId && (
        <Link
          data-testid={ATSConnectHiringRequestCreateReferenceContactIds.ADD_NEW_CONTACT_LINK}
          onClick={onAddClick}
          sx={{
            color: theme.palette.primary.dark,
            cursor: 'pointer',
            textDecoration: 'none',
            pt: 1,
          }}
        >
          + Add New Reference Contact
        </Link>
      )}
      <Box sx={{ pt: 2 }}>
        <ContactTemplateSelect
          dataTestId={ATSConnectHiringRequestRequestReferenceTestIds.REFERENCE_TEMPLATE_SELECT}
          formId="reference_template_id"
          handleChange={handleTemplateChange}
          isGetError={isTemplatesError}
          isLoading={isTemplatesLoading}
          items={referenceTemplates}
          labelText="Reference Check Form"
          placeholderText="Select Reference Check Form"
        />
      </Box>
    </Stack>
  );
};
